import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth';
import { InstanceLogo } from './InstanceLogo';
import { BRAND_NAME, CARD_PAYMENTS_ENABLED } from '../../utils/appConfig';
import { CommandPalette, PaletteItem, isMacPlatform } from './CommandPalette';
import { GuidedTour, TourStep, isStepAvailable } from './GuidedTour';
import { isTourSeen, markTourSeen } from './tourProgress';
import '../../assets/styles/navbar.css';

const getInitials = (name?: string | null, lastname?: string | null) => {
    const initials = `${(name ?? '').trim().charAt(0)}${(lastname ?? '').trim().charAt(0)}`.toUpperCase();
    return initials || '?';
};

// Opciones del menú que solo existen con pago con tarjeta
const CARD_ONLY_RESOURCES = ['link-pago-admin', 'create-link-pago'];

// Ícono (Bootstrap Icons) de cada opción según su código. Un grupo sin ícono propio usa el de su primera opción
const RESOURCE_ICONS: Record<string, string> = {
    'merchants': 'bi-shop',
    'merchant': 'bi-shop',
    'company': 'bi-building',
    'company-top': 'bi-building',
    'banks': 'bi-bank',
    'security': 'bi-shield-lock',
    'groups': 'bi-key',
    'users': 'bi-people',
    'products': 'bi-bag',
    'menu': 'bi-journal-text',
    'sections': 'bi-layout-three-columns',
    'tables': 'bi-grid-3x3-gap',
    'printers': 'bi-printer',
    'waiters': 'bi-person-badge',
    'suppliers': 'bi-truck',
    'inventory': 'bi-boxes',
    'stocks': 'bi-box-seam',
    'stock-movements': 'bi-arrow-left-right',
    'low-stock': 'bi-exclamation-triangle',
    'categories': 'bi-tags',
    'link-pago-admin': 'bi-link-45deg',
    'create-link-pago': 'bi-plus-circle',
    'accounting': 'bi-calculator',
    'loyalty': 'bi-gift',
    'loyalty/settings': 'bi-gift',
    'loyalty/rewards': 'bi-award',
    'loyalty/clients': 'bi-person-hearts',
    'rewards': 'bi-award',
    'reports': 'bi-bar-chart-line',
    'reports/transactions': 'bi-credit-card',
    'reports/electronic-invoices': 'bi-receipt',
    'reports/sales': 'bi-graph-up-arrow',
    'reports/merchant-payment': 'bi-cash-stack',
    'reports/merchant-features': 'bi-puzzle',
    'reports/bank-billing': 'bi-cash-coin',
    'refunds': 'bi-arrow-counterclockwise',
    'bank-features': 'bi-puzzle',
    'my-features': 'bi-puzzle',
    'electronic-invoice': 'bi-receipt-cutoff',
    'electronic-invoice-settings': 'bi-receipt-cutoff',
};
const DEFAULT_ICON = 'bi-circle';

// Grupo armado en el menú (no existe como recurso en la base de datos): junta opciones de primer nivel
// y se muestra al inicio, solo si el usuario tiene alguna de ellas
const SECURITY_GROUP = { id: -1, code: 'security', name: 'Seguridad', description: '', order: -1, parentId: null as any, children: [] };
const SECURITY_CODES = ['users', 'groups', 'banks'];

const iconFor = (code?: string | null) => {
    if (!code) return undefined;
    if (RESOURCE_ICONS[code]) return RESOURCE_ICONS[code];
    if (code.startsWith('reports/')) return 'bi-file-earmark-bar-graph';
    return undefined;
};

// Búsqueda sin distinguir mayúsculas ni tildes
const normalize = (text?: string | null) =>
    (text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Preferencia del menú compacto en escritorio (solo comodidad del usuario en este navegador)
const COLLAPSED_KEY = 'app-sidebar-collapsed';
const readCollapsed = () => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
};
const saveCollapsed = (value: boolean) => {
    try { localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0'); } catch { /* sin almacenamiento */ }
};

// Acciones rápidas de la paleta de comandos; solo aparecen si el usuario tiene el recurso
const QUICK_ACTIONS = [
    { resource: 'products', label: 'Nuevo producto', path: '/product', icon: 'bi-plus-lg' },
    { resource: 'products', label: 'Nuevo menú', path: '/menu', icon: 'bi-folder-plus' },
    { resource: 'stocks', label: 'Agregar producto al inventario', path: '/stock', icon: 'bi-box-arrow-in-down' },
    { resource: 'users', label: 'Nuevo usuario', path: '/user', icon: 'bi-person-plus' },
];

// Guía de uso: se abre sola la primera vez que el usuario entra al portal. Al terminarla u omitirla no se vuelve a
// mostrar (se guarda en su usuario); se puede repetir desde el menú del usuario
const PORTAL_TOUR = 'portal';

// Módulos que presenta la guía, en este orden; solo los que el usuario tiene en su menú (uno por grupo)
const TOUR_MODULES = [
    { resource: 'merchants', title: 'Comercios', content: 'Administre los comercios afiliados, sus cuentas y terminales.' },
    { resource: 'users', title: 'Seguridad', content: 'Cree usuarios, asígneles un grupo y defina a qué opciones del menú tiene acceso cada grupo.' },
    { resource: 'products', title: 'Productos y menús', content: 'Cree sus productos con precio e impuestos, organícelos en menús y categorías, e impórtelos o expórtelos en CSV.' },
    { resource: 'stocks', title: 'Inventario', content: 'Registre compras, entradas y salidas, consulte el historial de movimientos de cada producto (kardex) y vea qué productos tienen stock bajo.' },
    { resource: 'tables', title: 'Mesas y QR', content: 'Configure las secciones, mesas y meseros, y genere el código QR del menú para sus clientes.' },
    { resource: 'link-pago-admin', title: 'Links de pago', content: 'Cree links de pago y compártalos con sus clientes para cobrar con tarjeta.' },
    { resource: 'loyalty/settings', title: 'Programa de lealtad', content: 'Configure los puntos y premios para fidelizar a sus clientes.' },
    { resource: 'electronic-invoice-settings', title: 'Facturación electrónica', content: 'Configure los datos de su comercio ante Hacienda, sus sucursales y credenciales para emitir facturas electrónicas.' },
    { resource: 'accounting', title: 'Contabilidad', content: 'Lleve asientos, cuentas por cobrar y pagar, bancos, planilla y los estados financieros.' },
    { resource: 'reports/transactions', title: 'Reportes', content: 'Consulte transacciones, ventas, facturas electrónicas y más. Puede filtrar por fechas y exportar.' },
];

// Submenú flotante del modo compacto: un grupo con sus opciones, o el nombre de una opción suelta
interface Flyout {
    key: string;
    top: number;
    title: string;
    path?: string;
    items?: { id: number; name: string; code: string }[];
}

const DESKTOP_QUERY = '(min-width: 992px)';
const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
    useEffect(() => {
        const media = window.matchMedia(DESKTOP_QUERY);
        const onChange = () => setIsDesktop(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
};

/**
 * Navegación principal: menú lateral (agrupado, con buscador y modo compacto) y barra superior con el
 * título de la pantalla y el usuario. Envuelve el contenido de las pantallas del portal.
 */
export const Navbar = ({ children }: { children?: ReactNode }) => {
    const { user, logout, resources: allResources } = useContext(AuthContext);

    // Instancia sin banco: sin cobros con tarjeta, así que tampoco links de pago. Con banco, el link de pagos depende
    // de que la plataforma se lo asocie al banco (funcionalidad 'payment-link'): el API ya filtra el menú
    const resources = useMemo(() => CARD_PAYMENTS_ENABLED
        ? allResources
        : allResources.filter(r => !CARD_ONLY_RESOURCES.some(code => r.code === code || r.code?.startsWith(`${code}/`))),
        [allResources]);

    const [collapsedPref, setCollapsed] = useState(readCollapsed);
    const isDesktop = useIsDesktop();
    // El modo compacto solo aplica en escritorio; en móvil el menú se abre completo
    const collapsed = collapsedPref && isDesktop;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [flyout, setFlyout] = useState<Flyout | null>(null);
    const [tourOpen, setTourOpen] = useState(false);
    const flyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const topbarRef = useRef<HTMLElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    const flyoutRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const location = useLocation();

    const isRouteActive = (code: string) =>
        location.pathname === `/${code}` || location.pathname.startsWith(`/${code}/`);

    // Opciones del grupo Seguridad (en el orden de SECURITY_CODES)
    const securityResources = useMemo(() => SECURITY_CODES
        .map(code => resources.find(r => r.parentId === null && r.code === code))
        .filter(Boolean) as typeof resources, [resources]);

    // Recursos padres (Seguridad primero, sin repetir sus opciones)
    const parentResources = useMemo(() => {
        const parents = resources
            .filter(r => r.parentId === null && !securityResources.includes(r))
            .sort((a, b) => a.order - b.order);
        return securityResources.length > 0 ? [SECURITY_GROUP, ...parents] : parents;
    }, [resources, securityResources]);

    // Recursos hijos agrupados por padre
    const childResourcesMap = useMemo(() => {
        const map = resources
            .filter(r => r.parentId !== null)
            .reduce((acc: Record<number, any[]>, r) => {
                if (!acc[r.parentId]) acc[r.parentId] = [];
                acc[r.parentId].push(r);
                return acc;
            }, {});
        Object.keys(map).forEach(parentId => {
            map[parentId].sort((a, b) => a.order - b.order);
        });
        if (securityResources.length > 0) map[SECURITY_GROUP.id] = securityResources;
        return map;
    }, [resources, securityResources]);

    // Opción activa (y su grupo) para el título de la barra superior
    const current = useMemo(() => {
        for (const parent of parentResources) {
            const children = childResourcesMap[parent.id] || [];
            if (children.length === 0 && isRouteActive(parent.code)) return { parent: null, item: parent };
            const child = children.find(c => isRouteActive(c.code));
            if (child) return { parent, item: child };
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentResources, childResourcesMap, location.pathname]);

    // Al navegar: se cierra el menú en móvil, se abre el grupo de la pantalla actual y se cierran los desplegables
    useEffect(() => {
        setMobileOpen(false);
        setFlyout(null);
        if (current?.parent) setOpenGroups(groups => ({ ...groups, [current.parent.id]: true }));
        const bootstrap = (window as any).bootstrap;
        topbarRef.current?.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(toggle => {
            bootstrap?.Dropdown?.getInstance(toggle)?.hide();
        });
    }, [location.pathname, current?.parent?.id]);

    // En móvil, el contenido no se desplaza mientras el menú está abierto
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    // Ctrl/⌘ + K abre o cierra la paleta de comandos desde cualquier pantalla
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setFlyout(null);
                setPaletteOpen(open => !open);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // Pantallas y acciones de la paleta (las mismas opciones del menú)
    const paletteItems = useMemo<PaletteItem[]>(() => {
        const pages: PaletteItem[] = [{ key: 'page-home', label: 'Inicio', icon: 'bi-house', path: '/', kind: 'page' }];
        parentResources.forEach(parent => {
            const children = childResourcesMap[parent.id] || [];
            if (children.length === 0) {
                pages.push({ key: `page-${parent.id}`, label: parent.name, icon: iconFor(parent.code) ?? DEFAULT_ICON, path: `/${parent.code}`, kind: 'page' });
                return;
            }
            const groupIcon = iconFor(parent.code) ?? iconFor(children[0]?.code) ?? 'bi-folder2';
            children.forEach(child => pages.push({
                key: `page-${child.id}`, label: child.name, hint: parent.name,
                icon: iconFor(child.code) ?? groupIcon, path: `/${child.code}`, kind: 'page',
            }));
        });
        if (user?.merchant) {
            pages.push({ key: 'page-my-features', label: 'Mis funcionalidades', icon: 'bi-puzzle', path: '/my-features', kind: 'page' });
        }
        const actions: PaletteItem[] = QUICK_ACTIONS
            .filter(action => resources.some(r => r.code === action.resource))
            .map(action => ({ key: `action-${action.path}`, label: action.label, icon: action.icon, path: action.path, kind: 'action' }));
        return [...actions, ...pages];
    }, [parentResources, childResourcesMap, resources, user?.merchant]);

    const handlePaletteSelect = (item: PaletteItem) => {
        setPaletteOpen(false);
        navigate(item.path);
    };

    // ---------- Submenús flotantes (modo compacto)
    const cancelFlyoutClose = () => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
        flyoutTimer.current = null;
    };

    const scheduleFlyoutClose = () => {
        cancelFlyoutClose();
        flyoutTimer.current = setTimeout(() => setFlyout(null), 180);
    };

    const showFlyout = (data: Omit<Flyout, 'top'>, anchor: HTMLElement) => {
        cancelFlyoutClose();
        setFlyout({ ...data, top: Math.max(8, Math.min(anchor.getBoundingClientRect().top, window.innerHeight - 120)) });
    };

    // Solo en modo compacto: al pasar el mouse sobre una opción se muestra su flotante
    const flyoutHover = (data: Omit<Flyout, 'top'>) => collapsed ? {
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => showFlyout(data, e.currentTarget),
        onMouseLeave: scheduleFlyoutClose,
    } : {};

    // Se cierra al expandir el menú, con Esc o con un clic fuera
    useEffect(() => {
        if (!collapsed) setFlyout(null);
    }, [collapsed]);

    useEffect(() => {
        if (!flyout) return;
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!sidebarRef.current?.contains(target) && !flyoutRef.current?.contains(target)) setFlyout(null);
        };
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setFlyout(null); };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [flyout]);

    useEffect(() => () => cancelFlyoutClose(), []);

    // Grupos y opciones visibles según el buscador
    const term = normalize(search);
    const visibleGroups = useMemo(() => parentResources
        .map(parent => {
            const children = childResourcesMap[parent.id] || [];
            if (!term) return { parent, children };
            if (normalize(parent.name).includes(term)) return { parent, children };
            return { parent, children: children.filter(c => normalize(c.name).includes(term)) };
        })
        .filter(({ parent, children }) => {
            if (!term) return true;
            const hadChildren = (childResourcesMap[parent.id] || []).length > 0;
            return hadChildren ? children.length > 0 : normalize(parent.name).includes(term);
        }), [parentResources, childResourcesMap, term]);

    const toggleCollapsed = () => {
        setCollapsed(value => {
            saveCollapsed(!value);
            return !value;
        });
    };

    const toggleGroup = (id: number, flyoutData: Omit<Flyout, 'top'>, anchor: HTMLElement) => {
        // En modo compacto, el clic (teclado o pantalla táctil) abre o cierra el submenú flotante
        if (collapsed) {
            if (flyout?.key === flyoutData.key) setFlyout(null);
            else showFlyout(flyoutData, anchor);
            return;
        }
        setOpenGroups(groups => ({ ...groups, [id]: !groups[id] }));
    };

    // ---------- Guía de uso
    // Grupo (o opción de primer nivel) del menú donde está cada recurso, para resaltarlo
    const menuEntryOf = (code: string) => {
        if (SECURITY_CODES.includes(code) && securityResources.length > 0) return SECURITY_GROUP.id;
        const resource = resources.find(r => r.code === code);
        if (!resource) return null;
        const parentId = resource.parentId ?? resource.id;
        return parentResources.some(p => p.id === parentId) ? parentId : null;
    };

    const tourSteps = useMemo<TourStep[]>(() => {
        const moduleSteps: TourStep[] = [];
        const seen = new Set<number>();
        TOUR_MODULES.forEach(module => {
            const entry = menuEntryOf(module.resource);
            if (entry === null || seen.has(entry)) return;
            seen.add(entry);
            const parent = parentResources.find(p => p.id === entry);
            const children = childResourcesMap[entry] || [];
            moduleSteps.push({
                key: `module-${module.resource}`,
                target: `[data-tour-menu="${entry}"]`,
                area: 'sidebar',
                icon: iconFor(parent?.code) ?? iconFor(children[0]?.code) ?? iconFor(module.resource) ?? DEFAULT_ICON,
                title: parent?.name ?? module.title,
                content: module.content,
            });
        });

        return [
            {
                key: 'welcome', icon: 'bi-stars', title: `¡Bienvenido a ${BRAND_NAME}!`,
                content: 'Le mostramos en pocos pasos dónde encontrar cada cosa en el portal. Puede omitir la guía cuando quiera y volver a verla desde su menú de usuario.',
            },
            {
                key: 'menu', target: '.app-sidebar-nav', area: 'sidebar', icon: 'bi-list',
                title: 'Menú principal',
                content: 'Aquí están todas las opciones a las que tiene acceso, agrupadas por módulo. Haga clic en un grupo para ver sus opciones.',
            },
            {
                key: 'menu-search', target: '.app-sidebar-search', area: 'sidebar', icon: 'bi-funnel',
                title: 'Buscar en el menú',
                content: 'Escriba parte del nombre de una opción para filtrar el menú y llegar a ella más rápido.',
            },
            ...moduleSteps,
            {
                key: 'palette', target: '.app-topbar-search', area: 'topbar', icon: 'bi-search',
                title: 'Búsqueda rápida',
                content: `Desde cualquier pantalla, presione ${isMacPlatform() ? '⌘' : 'Ctrl'} + K para buscar una pantalla o una acción (por ejemplo, "Nuevo producto") e ir directo a ella.`,
            },
            {
                key: 'quick-access', target: '[data-tour="quick-access"]', area: 'content', icon: 'bi-lightning-charge',
                title: 'Accesos rápidos',
                content: 'En la pantalla de inicio tiene atajos a las tareas más comunes.',
            },
            {
                key: 'user', target: '.app-topbar-user', area: 'topbar', icon: 'bi-person-circle',
                title: 'Su cuenta',
                content: 'Aquí ve sus datos, sus funcionalidades y puede cerrar sesión. También puede volver a ver esta guía con "Ver guía de uso".',
            },
            {
                key: 'done', icon: 'bi-check2-circle', title: '¡Listo!',
                content: 'Ya conoce lo principal del portal. Si tiene dudas, puede repetir la guía cuando quiera desde su menú de usuario.',
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resources, parentResources, childResourcesMap, securityResources]);

    // Solo los pasos cuyo elemento está en pantalla (por ejemplo, los accesos rápidos solo existen en Inicio)
    const [visibleTourSteps, setVisibleTourSteps] = useState<TourStep[]>([]);

    const startTour = () => {
        setSearch('');
        setFlyout(null);
        // Espera a que el menú se dibuje sin filtro antes de revisar qué pasos tienen su elemento
        setTimeout(() => {
            setVisibleTourSteps(tourSteps.filter(isStepAvailable));
            setTourOpen(true);
        }, 0);
    };

    const finishTour = () => {
        setTourOpen(false);
        setMobileOpen(false);
        if (user) markTourSeen(user.id, PORTAL_TOUR);
    };

    // Primera vez: se abre sola cuando el menú ya cargó
    useEffect(() => {
        if (!user || resources.length === 0) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        isTourSeen(user.id, PORTAL_TOUR).then(seen => {
            if (!seen && !cancelled) timer = setTimeout(startTour, 800);
        });
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, resources.length > 0]);

    const handleLogout = () => {
        logout();
        navigate('/login', { replace: true });
    };

    const fullName = `${user?.name ?? ''} ${user?.lastname ?? ''}`.trim();
    const groupName = user?.groups?.[0]?.name;
    const merchantName = user?.merchant?.name ?? user?.bank?.description;   // usuarios de banco: su banco

    return (
        <div className={`app-shell ${collapsed ? 'app-shell-collapsed' : ''} ${mobileOpen ? 'app-shell-mobile-open' : ''}`}>

            {/* MENÚ LATERAL */}
            <aside ref={sidebarRef} className="app-sidebar" aria-label="Menú principal">
                <div className="app-sidebar-header">
                    <Link className="app-sidebar-brand" to="/" title={BRAND_NAME}>
                        <InstanceLogo white className="app-sidebar-logo" />
                    </Link>
                    <button type="button" className="app-sidebar-icon-btn d-lg-none" aria-label="Cerrar menú" onClick={() => setMobileOpen(false)}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="app-sidebar-search">
                    {collapsed ? (
                        <button type="button" className="app-sidebar-link" onClick={() => { setFlyout(null); setPaletteOpen(true); }}
                            {...flyoutHover({ key: 'search', title: `Buscar (${isMacPlatform() ? '⌘' : 'Ctrl'}+K)` })}>
                            <i className="bi bi-search app-sidebar-icon" />
                        </button>
                    ) : (
                        <div className="app-search-box">
                            <i className="bi bi-search" />
                            <input
                                type="search"
                                placeholder="Buscar opción..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape') setSearch(''); }}
                                aria-label="Buscar opción del menú"
                            />
                        </div>
                    )}
                </div>

                <nav className="app-sidebar-nav">
                    {(!term || normalize('Inicio').includes(term)) && (
                        <NavLink end className="app-sidebar-link" to="/" title={collapsed ? undefined : 'Inicio'}
                            {...flyoutHover({ key: 'home', title: 'Inicio', path: '/' })}>
                            <i className="bi bi-house app-sidebar-icon" />
                            <span className="app-sidebar-label">Inicio</span>
                        </NavLink>
                    )}

                    {visibleGroups.map(({ parent, children }) => {
                        const allChildren = childResourcesMap[parent.id] || [];

                        if (allChildren.length === 0) {
                            return (
                                <NavLink key={parent.id} data-tour-menu={parent.id} className="app-sidebar-link" to={`/${parent.code}`} title={collapsed ? undefined : parent.name}
                                    {...flyoutHover({ key: `item-${parent.id}`, title: parent.name, path: `/${parent.code}` })}>
                                    <i className={`bi ${iconFor(parent.code) ?? DEFAULT_ICON} app-sidebar-icon`} />
                                    <span className="app-sidebar-label">{parent.name}</span>
                                </NavLink>
                            );
                        }

                        const groupActive = allChildren.some(child => isRouteActive(child.code));
                        const open = !!term || !!openGroups[parent.id];
                        const groupIcon = iconFor(parent.code) ?? iconFor(allChildren[0]?.code) ?? 'bi-folder2';
                        const groupFlyout = { key: `group-${parent.id}`, title: parent.name, items: allChildren };
                        return (
                            <div key={parent.id} data-tour-menu={parent.id} className={`app-sidebar-group ${open ? 'open' : ''} ${groupActive ? 'group-active' : ''}`}>
                                <button
                                    type="button"
                                    className={`app-sidebar-link app-sidebar-group-toggle ${collapsed && flyout?.key === groupFlyout.key ? 'flyout-open' : ''}`}
                                    aria-expanded={collapsed ? flyout?.key === groupFlyout.key : open}
                                    aria-haspopup={collapsed ? 'menu' : undefined}
                                    title={collapsed ? undefined : parent.name}
                                    onClick={e => toggleGroup(parent.id, groupFlyout, e.currentTarget)}
                                    {...flyoutHover(groupFlyout)}
                                >
                                    <i className={`bi ${groupIcon} app-sidebar-icon`} />
                                    <span className="app-sidebar-label">{parent.name}</span>
                                    <i className="bi bi-chevron-down app-sidebar-chevron" />
                                </button>
                                {open && !collapsed && (
                                    <div className="app-sidebar-submenu">
                                        {children.map(child => (
                                            <NavLink key={child.id} className="app-sidebar-sublink" to={`/${child.code}`}>
                                                {child.name}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {term && visibleGroups.length === 0 && (
                        <div className="app-sidebar-empty">Sin opciones para “{search}”</div>
                    )}
                </nav>

                <button type="button" className="app-sidebar-collapse d-none d-lg-flex" onClick={toggleCollapsed}
                    title={collapsed ? 'Expandir menú' : 'Contraer menú'}>
                    <i className={`bi ${collapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'} app-sidebar-icon`} />
                    <span className="app-sidebar-label">Contraer menú</span>
                </button>
            </aside>

            {/* Submenú flotante del modo compacto */}
            {collapsed && flyout && (
                <div
                    ref={flyoutRef}
                    className={`app-flyout ${flyout.items ? '' : 'app-flyout-label'}`}
                    style={{ top: flyout.top, maxHeight: `calc(100vh - ${flyout.top + 12}px)` }}
                    role={flyout.items ? 'menu' : undefined}
                    onMouseEnter={cancelFlyoutClose}
                    onMouseLeave={scheduleFlyoutClose}
                >
                    {flyout.path
                        ? <NavLink end className="app-flyout-title app-flyout-title-link" to={flyout.path}>{flyout.title}</NavLink>
                        : <div className="app-flyout-title">{flyout.title}</div>}
                    {flyout.items?.map(item => (
                        <NavLink key={item.id} role="menuitem" className="app-flyout-link" to={`/${item.code}`}>
                            {item.name}
                        </NavLink>
                    ))}
                </div>
            )}

            <GuidedTour
                open={tourOpen}
                steps={visibleTourSteps}
                onAreaChange={area => { if (!isDesktop) setMobileOpen(area === 'sidebar'); }}
                onFinish={finishTour}
            />

            <CommandPalette open={paletteOpen} items={paletteItems} onClose={() => setPaletteOpen(false)} onSelect={handlePaletteSelect} />

            {/* Fondo oscuro del menú en móvil */}
            <div className="app-sidebar-backdrop d-lg-none" onClick={() => setMobileOpen(false)} />

            <div className="app-main">
                {/* BARRA SUPERIOR */}
                <header ref={topbarRef} className="app-topbar">
                    <button type="button" className="app-topbar-btn d-lg-none" aria-label="Abrir menú" onClick={() => setMobileOpen(true)}>
                        <i className="bi bi-list" />
                    </button>

                    <div className="app-topbar-title text-truncate">
                        {current?.parent && <span className="app-topbar-crumb">{current.parent.name}<i className="bi bi-chevron-right" /></span>}
                        <span className="app-topbar-page">{current?.item.name ?? (location.pathname === '/' ? 'Inicio' : '')}</span>
                    </div>

                    {/* PALETA DE COMANDOS */}
                    <button type="button" className="app-topbar-search ms-auto" onClick={() => setPaletteOpen(true)}
                        aria-label="Buscar pantalla o acción">
                        <i className="bi bi-search" />
                        <span className="d-none d-md-inline">Buscar...</span>
                        <kbd className="d-none d-md-inline">{isMacPlatform() ? '⌘' : 'Ctrl'} K</kbd>
                    </button>

                    {/* USUARIO */}
                    <div className="dropdown">
                        <button
                            type="button"
                            className="app-topbar-user dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title={fullName}
                        >
                            <span className="app-avatar">{getInitials(user?.name, user?.lastname)}</span>
                            <span className="d-none d-md-flex flex-column align-items-start text-start lh-sm">
                                <span className="fw-semibold text-truncate" style={{ maxWidth: 180 }}>{fullName || user?.username}</span>
                                {merchantName && <span className="small text-muted text-truncate" style={{ maxWidth: 180 }}>{merchantName}</span>}
                            </span>
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end app-user-menu">
                            <li className="px-2 pt-1 pb-2">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="app-avatar app-avatar-lg">{getInitials(user?.name, user?.lastname)}</span>
                                    <div className="text-truncate">
                                        <div className="fw-semibold text-truncate">{fullName || user?.username}</div>
                                        {user?.email && <div className="text-muted small text-truncate">{user.email}</div>}
                                    </div>
                                </div>
                                {(groupName || merchantName) && (
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                        {groupName && <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis">{groupName}</span>}
                                        {merchantName && <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis text-truncate" style={{ maxWidth: 200 }}>{merchantName}</span>}
                                    </div>
                                )}
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            {/* Funcionalidades del comercio (bancos y plataforma: Reportes y Funcionalidades en el menú) */}
                            {user?.merchant && (
                                <li>
                                    <Link className="dropdown-item" to="/my-features">
                                        <i className="bi bi-puzzle me-2" />Mis funcionalidades
                                    </Link>
                                </li>
                            )}
                            <li>
                                <button type="button" className="dropdown-item" onClick={startTour}>
                                    <i className="bi bi-signpost-split me-2" />Ver guía de uso
                                </button>
                            </li>
                            <li>
                                <button type="button" className="dropdown-item text-danger" onClick={handleLogout}>
                                    <i className="bi bi-box-arrow-right me-2" />Cerrar sesión
                                </button>
                            </li>
                        </ul>
                    </div>
                </header>

                <main className="app-content">
                    {children}
                </main>
            </div>
        </div>
    );
};
